import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Contract } from '../types';
import {
  contractViewState,
  contractWaitingOn,
  type ContractMissingItem,
  type ContractViewState,
} from '../utils/contractOverview';

const PILL_TONE: Record<ContractViewState, string> = { draft: 'secondary', waiting: 'warning', signed: 'success' };

/** The state as a pill, for the card header. */
export function ContractStatePill({ contract }: { contract: Contract }) {
  const { t } = useTranslation();
  const state = contractViewState(contract);
  return (
    <span className={`contract-group-status-pill is-${PILL_TONE[state]}`} data-testid="contract-overview-state">
      {t(`contracts.overview.state.${state}`)}
    </span>
  );
}

interface ContractOverviewProps {
  contract: Contract;
  isPerson: boolean;
  customerName: string;
  companyName: string;
  /** What a company's draft still lacks before it can be sent. */
  missing?: ContractMissingItem[];
  /** The next actions (page-owned buttons). */
  actions?: React.ReactNode;
}

/**
 * The top of the contract tab's Sammanfattning (ClickUp 869fa5qf8, 869fkjvdx):
 * before both have signed, where the contract stands and whose turn it is,
 * what a draft still lacks and the next actions; once both have signed,
 * "Signerat av båda parter" with each party's date. No step row.
 */
export function ContractOverview({ contract, isPerson, customerName, companyName, missing = [], actions }: ContractOverviewProps) {
  const { t, i18n } = useTranslation();
  const state = contractViewState(contract);
  const viewer = isPerson ? 'person' : 'company';
  const waitingOn = contractWaitingOn(contract);
  const names = { customer: customerName || t('contracts.overview.theCustomer'), company: companyName || t('contracts.overview.theCompany') };

  const what = state === 'waiting'
    ? t(`contracts.overview.what.waiting.${waitingOn}.${viewer}`, names)
    : t(`contracts.overview.what.draft.${viewer}`, names);
  const dayMonth = (value: string | null | undefined) =>
    // A no-break space keeps "16 okt." together on a narrow screen.
    value ? new Date(value).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'sv-SE', { day: 'numeric', month: 'short' }).replace(/ /g, '\u00a0') : '';

  return (
    <div className="contract-overview" data-testid="contract-overview" data-contract-state={state}>
      {state === 'signed' ? (
        // Signed by both: when each party signed, no step row (869fkjvdx).
        <div className="contract-overview-signed" data-testid="contract-overview-signed">
          <i className="feather-check-circle" aria-hidden="true"></i>
          <div>
            <p className="contract-overview-signed-title">{t('contracts.overview.signedBoth')}</p>
            <p className="contract-overview-signed-dates">
              {t('contracts.overview.signedDates', { customer: dayMonth(contract.personSignedAt), company: dayMonth(contract.companySignedAt) })}
            </p>
          </div>
        </div>
      ) : (
        <p className="contract-overview-what">{what}</p>
      )}
      {state === 'draft' && !isPerson && missing.length > 0 && (
        <div className="alert alert-warning mb-0 contract-overview-missing" role="status" data-testid="contract-overview-missing">
          <strong>{t('contracts.overview.missing.title')}</strong>
          <ul className="mb-0">
            {missing.map(item => <li key={item}>{t(`contracts.overview.missing.${item}`)}</li>)}
          </ul>
        </div>
      )}
      {actions && <div className="contract-overview-actions">{actions}</div>}
    </div>
  );
}
